import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LayoutDashboard, Users, Building2, Contact, ListChecks, Target, Radar,
  Mail, Megaphone, Bot, History, Plug, Settings as SettingsIcon, Flame,
  ShieldCheck, Sparkles, ChevronLeft, RefreshCw, Check, X, Pencil, Lock, Copy, ExternalLink, KanbanSquare, Upload, Trash2, CalendarClock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/salesdesk`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const GREEN = "#22c55e";
const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "prospects", label: "Prospects", icon: Users },
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "contacts", label: "Contacts", icon: Contact },
  { id: "queue", label: "Research Queue", icon: ListChecks },
  { id: "opportunities", label: "Opportunities", icon: Target },
  { id: "signals", label: "Buying Signals", icon: Radar },
  { id: "outreach", label: "Outreach", icon: Mail },
  { id: "pipeline", label: "Pipeline", icon: KanbanSquare },
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
  { id: "agents", label: "AI Agents", icon: Bot },
  { id: "activity", label: "Activity", icon: History },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const confColor = (c) => ({ CONFIRMED: "text-emerald-400 bg-emerald-500/10", LIKELY: "text-sky-400 bg-sky-500/10", POSSIBLE: "text-amber-400 bg-amber-500/10", UNKNOWN: "text-slate-400 bg-slate-500/10" }[c] || "text-slate-400 bg-slate-500/10");
const scoreBadge = (s) => s >= 80 ? { t: "HIGH PRIORITY", c: "bg-emerald-500 text-white" } : s >= 60 ? { t: "GOOD PROSPECT", c: "bg-sky-500 text-white" } : s >= 40 ? { t: "NURTURE", c: "bg-amber-500 text-slate-900" } : { t: "LOW", c: "bg-slate-600 text-white" };

const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-slate-700/60 bg-slate-800/40 ${className}`} {...rest}>{children}</div>
);
const Metric = ({ label, value, accent }) => (
  <Card className="p-4">
    <div className="text-2xl font-bold" style={{ color: accent || "#e2e8f0" }} data-testid={`metric-${label}`}>{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
  </Card>
);

export default function SalesDesk({ user }) {
  const [tab, setTab] = useState("dashboard");
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(null);
  const [prospectCampaign, setProspectCampaign] = useState(null);
  const role = user?.role;
  const allowed = ["admin", "zkteco_admin", "zkteco", "distributor_admin"].includes(role);

  useEffect(() => {
    if (!allowed) return;
    axios.get(`${API}/dashboard`, { headers: authHeaders() })
      .then((r) => setMode(r.data)).catch(() => {});
  }, [allowed, tab]);

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-300">
        <Card className="p-8 text-center max-w-md">
          <Lock className="h-8 w-8 text-slate-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Restricted</h2>
          <p className="text-sm text-slate-400">The AI Sales Desk is available to ZKTeco administrators only.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-slate-950/60 border-r border-slate-800 p-4 hidden md:flex flex-col">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="h-5 w-5" style={{ color: GREEN }} />
          <div>
            <div className="text-sm font-bold text-white leading-tight">AI Sales Desk</div>
            <div className="text-[10px] text-slate-500">ZKTeco UK · Internal</div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => {
            const Icon = n.icon; const active = tab === n.id;
            return (
              <button key={n.id} data-testid={`nav-${n.id}`}
                onClick={() => { setTab(n.id); setSelected(null); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}
                style={active ? { backgroundColor: "rgba(34,197,94,0.15)", boxShadow: `inset 3px 0 0 ${GREEN}` } : {}}>
                <Icon className="h-4 w-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="text-[10px] text-slate-600 mt-4">Signed in: {user?.email}</div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 p-6 overflow-x-hidden">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-white capitalize">{NAV.find(n => n.id === tab)?.label}</h1>
            <p className="text-xs text-slate-500">AI-powered B2B sales intelligence · {mode && mode.demo === false
              ? <span className="text-emerald-400 font-semibold">LIVE DATA (Apollo)</span>
              : <span className="text-amber-400 font-semibold">DEMO DATA</span>}</p>
          </div>
          {/* mobile nav */}
          <select className="md:hidden bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm" value={tab} onChange={(e) => { setTab(e.target.value); setSelected(null); }}>
            {NAV.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        </div>

        {selected
          ? <CompanyDetail id={selected} onBack={() => setSelected(null)} />
          : <Section tab={tab} onOpen={setSelected} prospectCampaign={prospectCampaign}
              goToProspects={(cid) => { setProspectCampaign(cid); setSelected(null); setTab("prospects"); }} />}
      </main>
    </div>
  );
}

function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${path}`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) { setData({ error: e.response?.data?.detail || "Failed to load" }); }
    setLoading(false);
  }, [path]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, reload: load };
}

function Section({ tab, onOpen, prospectCampaign, goToProspects }) {
  if (tab === "dashboard") return <Dashboard onOpen={onOpen} />;
  if (tab === "prospects" || tab === "companies") return <Prospects onOpen={onOpen} kind={tab} campaignId={prospectCampaign} />;
  if (tab === "opportunities") return <Opportunities onOpen={onOpen} />;
  if (tab === "contacts") return <Contacts />;
  if (tab === "queue") return <Queue onOpen={onOpen} />;
  if (tab === "signals") return <Signals />;
  if (tab === "outreach") return <Outreach />;
  if (tab === "pipeline") return <Pipeline onOpen={onOpen} />;
  if (tab === "campaigns") return <Campaigns goToProspects={goToProspects} />;
  if (tab === "agents") return <Agents />;
  if (tab === "activity") return <Activity />;
  if (tab === "integrations") return <Integrations />;
  if (tab === "settings") return <SettingsView />;
  return null;
}

function Dashboard({ onOpen }) {
  const { data, loading } = useFetch("/dashboard");
  if (loading || !data) return <Loader />;
  const m = data.metrics;
  return (
    <div className="space-y-6" data-testid="salesdesk-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Metric label="Total prospects" value={m.total_prospects} accent={GREEN} />
        <Metric label="High priority" value={m.high_priority} accent="#f87171" />
        <Metric label="Researched" value={m.prospects_researched} />
        <Metric label="Awaiting approval" value={m.emails_awaiting_approval} accent="#fbbf24" />
        <Metric label="Ready to send" value={m.emails_ready} />
        <Metric label="Contacts" value={m.total_contacts} />
      </div>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Opportunity Pipeline</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.pipeline).map(([k, v]) => (
            <div key={k} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-center min-w-[90px]">
              <div className="text-lg font-bold text-white">{v}</div>
              <div className="text-[10px] text-slate-400">{k}</div>
            </div>
          ))}
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Flame className="h-4 w-4 text-orange-400" /> AI Opportunity Feed</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {data.feed.map((c) => {
            const b = scoreBadge(c.opportunity_score);
            return (
              <Card key={c.id} className="p-4 hover:border-slate-500 cursor-pointer transition-colors" >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-white">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.sector} · {c.location}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded ${b.c}`}>{b.t}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="font-bold" style={{ color: GREEN }}>Score {c.opportunity_score}/100</span>
                  <span className="text-slate-500">AI confidence {c.ai_confidence}%</span>
                </div>
                <p className="text-xs text-slate-300 mt-2 line-clamp-3">{c.discovery_reason}</p>
                <button data-testid={`feed-open-${c.id}`} onClick={() => onOpen(c.id)} className="mt-3 text-xs font-semibold" style={{ color: GREEN }}>View research →</button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Table({ columns, rows, onRow, testid }) {
  return (
    <Card className="overflow-hidden" >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-700/60">
              {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className={`border-b border-slate-800/60 ${onRow ? "hover:bg-slate-800/40 cursor-pointer" : ""}`} onClick={onRow ? () => onRow(r) : undefined}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-300">{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No records.</div>}
    </Card>
  );
}

const STAGE_COLORS = {
  "To Contact": "border-slate-600", "Contacted": "border-sky-500/60",
  "Replied": "border-amber-500/60", "Won": "border-emerald-500/70", "Lost": "border-red-500/50",
};

function Pipeline({ onOpen }) {
  const { data, loading, reload } = useFetch("/pipeline");
  const setStage = async (id, stage) => {
    try { await axios.post(`${API}/companies/${id}/stage`, { stage }, { headers: authHeaders() }); reload(); }
    catch (e) { alert("Could not update stage."); }
  };
  const setFollowUp = async (id, next_follow_up) => {
    try { await axios.post(`${API}/companies/${id}/stage`, { stage: "Contacted", next_follow_up }, { headers: authHeaders() }); reload(); }
    catch (e) { alert("Could not set follow-up."); }
  };
  if (loading || !data) return <Loader />;
  const stages = data.stages;
  const byStage = (s) => data.companies.filter((c) => (c.pipeline_stage || "To Contact") === s);
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(200px, 1fr))` }} data-testid="pipeline-board">
      {stages.map((s) => {
        const items = byStage(s);
        return (
          <div key={s} className={`rounded-lg border-t-2 ${STAGE_COLORS[s] || "border-slate-600"} bg-slate-900/40 p-2`} data-testid={`pipeline-col-${s.replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between px-1 mb-2">
              <span className="text-xs font-bold text-white uppercase tracking-wide">{s}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{items.length}</span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-auto">
              {items.map((c) => {
                const overdue = c.next_follow_up && new Date(c.next_follow_up) < new Date();
                return (
                  <div key={c.id} className="rounded bg-slate-800/80 border border-slate-700 p-2" data-testid={`pipeline-card-${c.id}`}>
                    <button onClick={() => onOpen(c.id)} className="text-sm text-white font-medium text-left hover:underline">{c.name}</button>
                    <div className="text-[11px] text-slate-400 mt-0.5">Score {c.opportunity_score} · {c.location}</div>
                    {c.next_follow_up && <div className={`text-[11px] mt-1 flex items-center gap-1 ${overdue ? "text-red-400" : "text-slate-400"}`}><CalendarClock className="h-3 w-3" /> {overdue ? "Overdue: " : "Follow up: "}{c.next_follow_up}</div>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      <select data-testid={`pipeline-stage-${c.id}`} value={c.pipeline_stage || "To Contact"} onChange={(e) => setStage(c.id, e.target.value)} className="text-[11px] bg-slate-900 border border-slate-700 rounded px-1 py-1 text-slate-200">
                        {stages.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                      <input type="date" data-testid={`pipeline-followup-${c.id}`} value={(c.next_follow_up || "").slice(0, 10)} onChange={(e) => setFollowUp(c.id, e.target.value)} className="text-[11px] bg-slate-900 border border-slate-700 rounded px-1 py-1 text-slate-200" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Prospects({ onOpen, campaignId }) {
  const [sort, setSort] = useState("opportunity_score");
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState("");
  const path = `/prospects?sort=${sort}${campaignId ? `&campaign_id=${campaignId}` : ""}`;
  const { data, loading, reload } = useFetch(path, [sort, campaignId]);
  const sorts = [
    ["opportunity_score", "Opportunity Score"], ["ai_confidence", "AI Confidence"],
    ["recency", "Recency"], ["product_fit", "Product Fit"], ["signal_strength", "Buying Signal Strength"],
  ];
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkEnrich = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Unlock verified emails for all locked contacts of ${selected.length} companies? This uses 1 Apollo credit per contact.`)) return;
    setBulkBusy("enrich");
    try { const r = await axios.post(`${API}/companies/bulk-enrich`, { company_ids: selected }, { headers: authHeaders() });
      alert(`Enriched ${r.data.enriched} emails (${r.data.no_match} no-match, ${r.data.failed} failed). ${r.data.credits_used} credits used.`); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Bulk enrich failed."); }
    setBulkBusy("");
  };
  const bulkOutreach = async () => {
    if (!selected.length) return;
    setBulkBusy("outreach");
    try { const r = await axios.post(`${API}/companies/bulk-outreach`, { company_ids: selected }, { headers: authHeaders() });
      alert(`Drafted ${r.data.drafted} outreach emails (see Outreach tab, Awaiting Approval).`); }
    catch (e) { alert(e.response?.data?.detail || "Bulk outreach failed."); }
    setBulkBusy("");
  };
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Permanently delete ${selected.length} selected company/companies and their contacts? This cannot be undone.`)) return;
    setBulkBusy("delete");
    try { const r = await axios.post(`${API}/companies/bulk-delete`, { company_ids: selected }, { headers: authHeaders() });
      alert(`Deleted ${r.data.deleted} prospect(s).`); setSelected([]); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Delete failed."); }
    setBulkBusy("");
  };
  const src = (r) => (r.source === "Apollo"
    ? <span className="text-[10px] px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">Apollo</span>
    : <span className="text-[10px] px-2 py-0.5 rounded bg-slate-600/40 text-slate-300">Demo</span>);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Sort by</span>
          <select data-testid="prospects-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
            {sorts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {campaignId && <span className="text-xs text-emerald-400">Filtered to selected campaign</span>}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto" data-testid="bulk-bar">
            <span className="text-xs text-slate-300">{selected.length} selected</span>
            <button data-testid="bulk-enrich" onClick={bulkEnrich} disabled={bulkBusy} className="text-xs px-3 py-1.5 rounded border border-sky-500/50 text-sky-300 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> {bulkBusy === "enrich" ? "Unlocking…" : "Unlock emails"}</button>
            <button data-testid="bulk-outreach" onClick={bulkOutreach} disabled={bulkBusy} className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1" style={{ backgroundColor: GREEN }}><Sparkles className="h-3.5 w-3.5" /> {bulkBusy === "outreach" ? "Drafting…" : "Draft outreach"}</button>
            <button data-testid="bulk-delete" onClick={bulkDelete} disabled={bulkBusy} className="text-xs px-3 py-1.5 rounded border border-red-500/50 text-red-400 flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> {bulkBusy === "delete" ? "Deleting…" : "Delete"}</button>
            <button onClick={() => setSelected([])} className="text-xs text-slate-400 hover:text-white">Clear</button>
          </div>
        )}
      </div>
      {loading || !data ? <Loader /> : (
        <Table testid="prospects-table" onRow={(r) => onOpen(r.id)} rows={data.prospects} columns={[
          { key: "sel", label: "", render: (r) => <input type="checkbox" data-testid={`select-${r.id}`} checked={selected.includes(r.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(r.id)} /> },
          { key: "name", label: "Company", render: (r) => <span className="text-white font-medium">{r.name}</span> },
          { key: "company_kind", label: "Kind", render: (r) => <span className="text-xs text-slate-300">{r.company_kind || r.sector || "—"}</span> },
          { key: "location", label: "Location" },
          { key: "buying_signal", label: "Buying signal", render: (r) => <span className="text-xs text-slate-400">{(r.buying_signal || r.discovery_reason || "").slice(0, 60)}</span> },
          { key: "recommended_product", label: "Rec. product", render: (r) => <span className="text-xs text-emerald-300">{r.recommended_product || "—"}</span> },
          { key: "opportunity_score", label: "Score", render: (r) => <span className="font-bold" style={{ color: GREEN }}>{r.opportunity_score}</span> },
          { key: "ai_confidence", label: "Conf.", render: (r) => <span className="text-xs">{r.ai_confidence}%</span> },
          { key: "source", label: "Source", render: src },
          { key: "status", label: "Status" },
        ]} />
      )}
    </div>
  );
}

function Opportunities({ onOpen }) {
  const { data, loading } = useFetch("/opportunities");
  if (loading || !data) return <Loader />;
  return <Table testid="opportunities-table" onRow={(r) => onOpen(r.id)} rows={data.opportunities} columns={[
    { key: "name", label: "Company", render: (r) => <span className="text-white font-medium">{r.name}</span> },
    { key: "opportunity_score", label: "Score", render: (r) => <span className="font-bold" style={{ color: GREEN }}>{r.opportunity_score}</span> },
    { key: "ai_confidence", label: "AI Conf.", render: (r) => `${r.ai_confidence}%` },
    { key: "discovery_reason", label: "Trigger", render: (r) => <span className="text-xs">{r.discovery_reason}</span> },
    { key: "status", label: "Status" },
  ]} />;
}

function Contacts() {
  const { data, loading, reload } = useFetch("/contacts");
  const [busy, setBusy] = useState(null);
  const unlock = async (id) => {
    setBusy(id);
    try {
      const res = await axios.post(`${API}/contacts/${id}/enrich-email`, {}, { headers: authHeaders() });
      if (res.data?.status === "no_match") alert(res.data.message || "Apollo has no verified email for this contact.");
    } catch (e) { alert(e.response?.data?.detail || "Unlock failed."); }
    setBusy(null);
    reload();
  };
  if (loading || !data) return <Loader />;
  return <Table testid="contacts-table" rows={data.contacts} columns={[
    { key: "full_name", label: "Name", render: (r) => <span className="text-white font-medium">{r.full_name}</span> },
    { key: "job_title", label: "Title" },
    { key: "company", label: "Company" },
    { key: "email", label: "Email", render: (r) => r.email_locked
        ? <span className="text-amber-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</span>
        : (r.verification_status === "UNVERIFIED" ? <span className="text-amber-400">{r.email} (UNVERIFIED)</span> : r.email) },
    { key: "phone", label: "Phone" },
    { key: "verification_status", label: "Verified", render: (r) => <span className={`text-[10px] px-2 py-0.5 rounded ${confColor(r.verification_status)}`}>{r.verification_status}</span> },
    { key: "actions", label: "", render: (r) => r.email_locked
        ? <button data-testid={`unlock-contact-${r.id}`} onClick={() => unlock(r.id)} disabled={busy === r.id}
            className="text-xs font-semibold flex items-center gap-1 text-sky-400 disabled:opacity-50 whitespace-nowrap">
            <Lock className="h-3.5 w-3.5" /> {busy === r.id ? "Unlocking…" : "Unlock email (1 credit)"}
          </button>
        : null },
  ]} />;
}

function Queue({ onOpen }) {
  const { data, loading } = useFetch("/research-queue");
  if (loading || !data) return <Loader />;
  return <Table testid="queue-table" onRow={(r) => onOpen(r.company_id)} rows={data.queue} columns={[
    { key: "company", label: "Company", render: (r) => <span className="text-white font-medium">{r.company}</span> },
    { key: "discovery_reason", label: "Discovery reason", render: (r) => <span className="text-xs">{r.discovery_reason}</span> },
    { key: "research_status", label: "Research" },
    { key: "contact_status", label: "Contact" },
    { key: "opportunity_score", label: "Score", render: (r) => <span className="font-bold" style={{ color: GREEN }}>{r.opportunity_score}</span> },
    { key: "agent", label: "Agent" },
  ]} />;
}

function Signals() {
  const { data, loading } = useFetch("/signals");
  if (loading || !data) return <Loader />;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="signals-list">
      {data.signals.map((s) => (
        <Card key={s.id} className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-700 text-slate-200">{s.category}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded ${confColor(s.confidence)}`}>{s.confidence}</span>
          </div>
          <p className="text-sm text-white mt-2">{s.finding}</p>
          <div className="text-xs text-slate-400 mt-1">{s.company}</div>
          <div className="flex gap-3 mt-2 text-[11px] text-slate-400">
            <span>Strength <b className="text-white">{s.strength}</b></span>
            <span>Fit <b className="text-white">{s.product_fit}</b></span>
            <span>{s.recency}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Outreach() {
  const [status, setStatus] = useState("Awaiting Approval");
  const { data, loading, reload } = useFetch(`/outreach?status=${encodeURIComponent(status)}`, [status]);
  const [editing, setEditing] = useState(null);
  const tabs = ["Awaiting Approval", "Ready to Send", "Rejected", "Sent"];

  const act = async (id, action) => {
    try { await axios.post(`${API}/outreach/${id}/${action}`, {}, { headers: authHeaders() }); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Action failed"); }
  };
  const copyEmail = async (o) => {
    try { await navigator.clipboard.writeText(`Subject: ${o.subject}\n\n${o.body}`); alert("Email copied to clipboard."); }
    catch { alert("Copy failed — please select and copy manually."); }
  };
  const openMail = (o) => {
    window.location.href = `mailto:${o.to_email || ""}?subject=${encodeURIComponent(o.subject)}&body=${encodeURIComponent(o.body)}`;
  };
  const saveEdit = async () => {
    await axios.put(`${API}/outreach/${editing.id}`, { subject: editing.subject, body: editing.body }, { headers: authHeaders() });
    setEditing(null); reload();
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map((t) => (
          <button key={t} data-testid={`outreach-tab-${t}`} onClick={() => setStatus(t)}
            className={`text-xs px-3 py-1.5 rounded-lg border ${status === t ? "text-white border-emerald-500 bg-emerald-500/15" : "text-slate-400 border-slate-700 hover:text-white"}`}>{t}</button>
        ))}
      </div>
      {loading || !data ? <Loader /> : (
        <div className="space-y-3" data-testid="outreach-list">
          {data.outreach.map((o) => (
            <Card key={o.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold">{o.subject}</div>
                  <div className="text-xs text-slate-400">To: {o.contact_name} · {o.company_name} · {o.to_email}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-200 shrink-0">{o.status}</span>
              </div>
              {editing?.id === o.id ? (
                <div className="mt-3 space-y-2">
                  <input className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
                  <textarea className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200 h-40" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="text-xs px-3 py-1.5 rounded bg-emerald-500 text-white font-semibold">Save</button>
                    <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300">Cancel</button>
                  </div>
                </div>
              ) : (
                <pre className="text-xs text-slate-300 whitespace-pre-wrap mt-3 font-sans">{o.body}</pre>
              )}
              {o.status === "Awaiting Approval" && editing?.id !== o.id && (
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-${o.id}`} onClick={() => act(o.id, "approve")} className="text-xs px-3 py-1.5 rounded bg-emerald-500 text-white font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Approve → Ready to Send</button>
                  <button onClick={() => setEditing({ id: o.id, subject: o.subject, body: o.body })} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button data-testid={`reject-${o.id}`} onClick={() => act(o.id, "reject")} className="text-xs px-3 py-1.5 rounded border border-red-500/50 text-red-400 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Reject</button>
                </div>
              )}
              {o.status === "Ready to Send" && editing?.id !== o.id && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`copy-${o.id}`} onClick={() => copyEmail(o)} className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1" style={{ backgroundColor: GREEN }}><Copy className="h-3.5 w-3.5" /> Copy email</button>
                    <button data-testid={`openmail-${o.id}`} onClick={() => openMail(o)} className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-200 flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Open in email client</button>
                    <button onClick={() => setEditing({ id: o.id, subject: o.subject, body: o.body })} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button data-testid={`contacted-${o.id}`} onClick={() => act(o.id, "mark-contacted")} className="text-xs px-3 py-1.5 rounded border border-emerald-500/50 text-emerald-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Mark as Contacted</button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">Approved &amp; ready. Copy or open in your email client, send it manually, then mark as contacted. No automatic sending.</p>
                </div>
              )}
              {o.status === "Sent" && <p className="text-[11px] text-emerald-400 mt-3">✔ Sent manually · company marked as Contacted.</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_CRITERIA = {
  geography: { country: "United Kingdom", region: "", county: "", city: "", postcode: "", radius_miles: "" },
  company: { company_type: "", sectors: [], industries: [], employee_min: "", employee_max: "", revenue: "", growth: "", num_locations: "", company_age: "" },
  person: { job_titles: [], seniorities: [], departments: [], functions: [], locations: [] },
  technologies: { include: [], exclude: [] },
  buying_signals: [], signal_recency: "Last 12 months",
  zkteco_products: [], opportunity_criteria: "", min_score: 70,
};
const toArr = (s) => (s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
const fromArr = (a) => (a || []).join(", ");
const RECENCY = ["Last 30 days", "Last 90 days", "Last 6 months", "Last 12 months", "Any time"];

const Field = ({ label, value, onChange, placeholder, testid, textarea }) => (
  <label className="block">
    <span className="text-[11px] text-slate-400">{label}</span>
    {textarea
      ? <textarea data-testid={testid} value={value} onChange={onChange} placeholder={placeholder} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white h-20" />
      : <input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />}
  </label>
);
const Group = ({ title, children }) => (
  <Card className="p-4">
    <h4 className="text-xs font-bold text-white uppercase tracking-wide mb-3">{title}</h4>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </Card>
);

function CampaignBuilder({ initial, saving, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const norm = (o, keys) => { const r = { ...o }; keys.forEach((k) => { if (r[k] == null) r[k] = ""; }); return r; };
  const [crit, setCrit] = useState({ ...EMPTY_CRITERIA, ...(initial?.criteria || {}),
    geography: norm({ ...EMPTY_CRITERIA.geography, ...(initial?.criteria?.geography || {}) }, ["country", "region", "county", "city", "postcode", "radius_miles"]),
    company: norm({ ...EMPTY_CRITERIA.company, ...(initial?.criteria?.company || {}) }, ["company_type", "employee_min", "employee_max", "revenue", "growth", "num_locations", "company_age"]),
    person: { ...EMPTY_CRITERIA.person, ...(initial?.criteria?.person || {}) },
    technologies: { ...EMPTY_CRITERIA.technologies, ...(initial?.criteria?.technologies || {}) },
    opportunity_criteria: initial?.criteria?.opportunity_criteria || "",
    signal_recency: initial?.criteria?.signal_recency || "Last 12 months",
    min_score: initial?.criteria?.min_score ?? 70,
  });
  const g = crit.geography, co = crit.company, pe = crit.person, te = crit.technologies;
  const setG = (k, v) => setCrit({ ...crit, geography: { ...g, [k]: v } });
  const setCo = (k, v) => setCrit({ ...crit, company: { ...co, [k]: v } });
  const setPe = (k, v) => setCrit({ ...crit, person: { ...pe, [k]: v } });
  const setTe = (k, v) => setCrit({ ...crit, technologies: { ...te, [k]: v } });

  const submit = () => {
    if (!name.trim()) { alert("Please give the campaign a name."); return; }
    const clean = JSON.parse(JSON.stringify(crit));
    clean.company.employee_min = co.employee_min ? Number(co.employee_min) : null;
    clean.company.employee_max = co.employee_max ? Number(co.employee_max) : null;
    clean.geography.radius_miles = g.radius_miles ? Number(g.radius_miles) : null;
    clean.min_score = Number(crit.min_score) || 70;
    onSave({ name, criteria: clean });
  };

  return (
    <Card className="p-4 border-emerald-600/40" >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">{initial?.id ? "Edit Campaign" : "Interpreted Criteria — review & edit before saving"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <div className="mb-3">
        <Field label="Campaign name" testid="builder-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. UK Universities — New Build Access Control" />
      </div>
      <div className="space-y-3">
        <Group title="Geography">
          <Field label="Country" value={g.country} onChange={(e) => setG("country", e.target.value)} />
          <Field label="Region" value={g.region} onChange={(e) => setG("region", e.target.value)} />
          <Field label="County" value={g.county} onChange={(e) => setG("county", e.target.value)} />
          <Field label="City" value={g.city} onChange={(e) => setG("city", e.target.value)} />
          <Field label="Postcode" value={g.postcode} onChange={(e) => setG("postcode", e.target.value)} />
          <Field label="Radius (miles)" value={g.radius_miles} onChange={(e) => setG("radius_miles", e.target.value)} />
        </Group>
        <Group title="Company">
          <Field label="Company type" value={co.company_type} onChange={(e) => setCo("company_type", e.target.value)} placeholder="e.g. University, NHS Trust, Security Integrator" />
          <Field label="Sectors (comma separated)" value={fromArr(co.sectors)} onChange={(e) => setCo("sectors", toArr(e.target.value))} />
          <Field label="Industries (comma separated)" value={fromArr(co.industries)} onChange={(e) => setCo("industries", toArr(e.target.value))} />
          <Field label="Employees min" value={co.employee_min} onChange={(e) => setCo("employee_min", e.target.value)} />
          <Field label="Employees max" value={co.employee_max} onChange={(e) => setCo("employee_max", e.target.value)} />
          <Field label="Estimated revenue" value={co.revenue} onChange={(e) => setCo("revenue", e.target.value)} />
          <Field label="Company growth" value={co.growth} onChange={(e) => setCo("growth", e.target.value)} />
          <Field label="Number of locations" value={co.num_locations} onChange={(e) => setCo("num_locations", e.target.value)} />
        </Group>
        <Group title="Person / Decision makers">
          <Field label="Job titles (comma separated)" testid="builder-titles" value={fromArr(pe.job_titles)} onChange={(e) => setPe("job_titles", toArr(e.target.value))} placeholder="Managing Director, Estates Director, Head of Security" />
          <Field label="Seniorities" value={fromArr(pe.seniorities)} onChange={(e) => setPe("seniorities", toArr(e.target.value))} placeholder="Director, Head, VP, C-Suite" />
          <Field label="Departments" value={fromArr(pe.departments)} onChange={(e) => setPe("departments", toArr(e.target.value))} />
          <Field label="Functions" value={fromArr(pe.functions)} onChange={(e) => setPe("functions", toArr(e.target.value))} />
          <Field label="Person locations" value={fromArr(pe.locations)} onChange={(e) => setPe("locations", toArr(e.target.value))} />
        </Group>
        <Group title="Technology (existing manufacturers)">
          <Field label="Include (uses these)" value={fromArr(te.include)} onChange={(e) => setTe("include", toArr(e.target.value))} placeholder="Gallagher, Paxton, HID" />
          <Field label="Exclude / gap (does NOT use)" value={fromArr(te.exclude)} onChange={(e) => setTe("exclude", toArr(e.target.value))} placeholder="e.g. entrance control" />
        </Group>
        <Group title="Buying signals & products">
          <Field label="Buying signals (comma separated)" value={fromArr(crit.buying_signals)} onChange={(e) => setCrit({ ...crit, buying_signals: toArr(e.target.value) })} placeholder="New building, Contract win, Recruitment, Expansion" />
          <label className="block">
            <span className="text-[11px] text-slate-400">Signal recency</span>
            <select value={crit.signal_recency} onChange={(e) => setCrit({ ...crit, signal_recency: e.target.value })} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
              {RECENCY.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <Field label="ZKTeco products (comma separated)" value={fromArr(crit.zkteco_products)} onChange={(e) => setCrit({ ...crit, zkteco_products: toArr(e.target.value) })} placeholder="Access Control, Turnstiles, Speed Lanes, ANPR" />
          <Field label="Minimum opportunity score" value={crit.min_score} onChange={(e) => setCrit({ ...crit, min_score: e.target.value })} />
        </Group>
        <Card className="p-4">
          <Field label="AI opportunity criteria (what kind of opportunity are you looking for?)" textarea testid="builder-opportunity" value={crit.opportunity_criteria || ""} onChange={(e) => setCrit({ ...crit, opportunity_criteria: e.target.value })} placeholder="e.g. Find universities planning new buildings where access or entrance control may be required in the next 12 months." />
        </Card>
      </div>
      <div className="flex gap-2 mt-4">
        <button data-testid="save-campaign" onClick={submit} disabled={saving} className="text-sm px-4 py-2 rounded font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: GREEN }}>
          <ShieldCheck className="h-4 w-4" /> {saving ? "Saving…" : "Save Campaign"}
        </button>
        <button onClick={onCancel} className="text-sm px-4 py-2 rounded border border-slate-700 text-slate-300">Cancel</button>
      </div>
    </Card>
  );
}

function Campaigns({ goToProspects }) {
  const { data, loading, reload } = useFetch("/campaigns");
  const [nlQuery, setNlQuery] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [builder, setBuilder] = useState(null);   // {name, criteria, id?} or null
  const [saving, setSaving] = useState(false);
  const [runState, setRunState] = useState({});    // {campaignId: "phase text"}
  const [depth, setDepth] = useState({});          // {campaignId: 25|50|100|250}

  const interpret = async () => {
    if (!nlQuery.trim()) return;
    setInterpreting(true);
    try {
      const res = await axios.post(`${API}/interpret`, { query: nlQuery }, { headers: authHeaders() });
      const jobId = res.data.job_id;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const j = (await axios.get(`${API}/jobs/${jobId}`, { headers: authHeaders() })).data;
          if (j.status === "done" && j.result) {
            setBuilder({ name: j.result.campaign_name, criteria: j.result.criteria });
            setInterpreting(false);
          } else if (j.status === "error") {
            alert(j.error || "AI interpretation failed."); setInterpreting(false);
          } else if (tries > 40) {
            alert("Interpretation is taking too long. Please try again or use Build manually."); setInterpreting(false);
          } else {
            setTimeout(poll, 2500);
          }
        } catch (e) { alert("Could not read interpretation status."); setInterpreting(false); }
      };
      setTimeout(poll, 2000);
    } catch (e) { alert(e.response?.data?.detail || "AI interpretation failed."); setInterpreting(false); }
  };

  const save = async ({ name, criteria }) => {
    setSaving(true);
    try {
      if (builder?.id) {
        await axios.put(`${API}/campaigns/${builder.id}`, { name, criteria, nl_query: nlQuery || builder.nl_query }, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/campaigns`, { name, criteria, nl_query: nlQuery }, { headers: authHeaders() });
      }
      setBuilder(null); setNlQuery(""); reload();
    } catch (e) { alert(e.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };

  const action = async (id, verb) => {
    try { await axios.post(`${API}/campaigns/${id}/${verb}`, {}, { headers: authHeaders() }); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Action failed."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await axios.delete(`${API}/campaigns/${id}`, { headers: authHeaders() }); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Delete failed."); }
  };
  const clearDemo = async () => {
    if (!window.confirm("Remove all demo prospects, contacts and sample campaigns? Your live Apollo results and real campaigns are kept. Demo data will NOT come back automatically.")) return;
    try {
      await axios.post(`${API}/clear-demo`, {}, { headers: authHeaders() });
      alert("Demo data cleared. Create a campaign and run a real Apollo search.");
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Could not clear demo data."); }
  };
  const populateDemo = async () => {
    try {
      const res = await axios.post(`${API}/restore-demo`, {}, { headers: authHeaders() });
      alert(res.data.message || "Demo data populated.");
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Could not populate demo data."); }
  };
  const tidyContacts = async () => {
    if (!window.confirm("Tidy up the desk? This removes leftover companies from deleted campaigns and trims every company to its 5 most relevant decision-makers (drops junk roles). Your campaigns are kept.")) return;
    try {
      const res = await axios.post(`${API}/maintenance/tidy-contacts?max_per_company=5`, {}, { headers: authHeaders() });
      const d = res.data || {};
      alert(`Tidied. Removed ${d.orphan_companies_removed || 0} leftover companies and ${(d.orphan_contacts_removed || 0) + (d.contacts_removed || 0)} surplus contacts.`);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Could not tidy contacts."); }
  };
  const exportCsv = async (c) => {
    try {
      const res = await axios.get(`${API}/campaigns/${c.id}/export.csv`, { headers: authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${(c.name || "campaign").replace(/\s+/g, "_")}_prospects.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert("Export failed."); }
  };

  const run = async (c) => {
    const limit = depth[c.id] || 100;
    setRunState((s) => ({ ...s, [c.id]: "Starting…" }));
    try {
      const res = await axios.post(`${API}/campaigns/${c.id}/find-prospects?limit=${limit}`, {}, { headers: authHeaders() });
      const jobId = res.data.job_id;
      let fails = 0, ticks = 0;
      const poll = async () => {
        ticks += 1;
        try {
          const j = (await axios.get(`${API}/jobs/${jobId}`, { headers: authHeaders() })).data;
          fails = 0;
          if (j.status === "done") {
            setRunState((s) => ({ ...s, [c.id]: null }));
            alert(`Done: ${j.companies_added} companies and ${j.contacts_added} contacts found & AI-qualified${j.suppressed_skipped ? ` (${j.suppressed_skipped} suppressed)` : ""}. Opening results…`);
            reload(); goToProspects(c.id);
          } else if (j.status === "error") {
            setRunState((s) => ({ ...s, [c.id]: null }));
            alert(`Prospect search failed: ${j.error}`);
          } else if (ticks > 150) {
            setRunState((s) => ({ ...s, [c.id]: null }));
            alert("This search is taking unusually long. It may still finish — check the Results shortly.");
          } else {
            setRunState((s) => ({ ...s, [c.id]: `${j.phase}… (${j.companies_added} companies)` }));
            setTimeout(poll, 4000);
          }
        } catch (err) {
          // Transient 502/network blips during long AI scoring — keep polling
          fails += 1;
          setRunState((s) => ({ ...s, [c.id]: `Working… (still processing)` }));
          if (fails < 40) setTimeout(poll, 4000);
          else { setRunState((s) => ({ ...s, [c.id]: null })); alert("Lost connection to the search job. It may still finish — check Results shortly."); }
        }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      setRunState((s) => ({ ...s, [c.id]: null }));
      alert(e.response?.data?.detail || "Could not start prospect search.");
    }
  };

  if (loading || !data) return <Loader />;
  return (
    <div className="space-y-4">
      {/* Natural language search */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: GREEN }} /> What are you looking for?</h3>
            <p className="text-xs text-slate-500 mb-2">Describe your ideal prospects in plain English — the AI turns it into editable search criteria for ANY audience (integrators, universities, NHS, architects, developers, end users…).</p>
          </div>
          <div className="shrink-0 flex gap-2">
            <button data-testid="populate-demo" onClick={populateDemo} className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-200 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Populate demo data</button>
            <button data-testid="clear-demo" onClick={clearDemo} className="text-xs px-3 py-1.5 rounded border border-red-500/50 text-red-400 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Clear demo data</button>
            <button data-testid="tidy-contacts" onClick={tidyContacts} className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-200 flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Tidy contacts</button>
          </div>
        </div>
        <textarea data-testid="nl-query" value={nlQuery} onChange={(e) => setNlQuery(e.target.value)}
          placeholder="e.g. Find UK security integrators between 20 and 200 employees working on university projects. Prioritise companies using Gallagher or Paxton and look for evidence they are expanding their access-control business."
          className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white h-24" />
        <div className="flex gap-2 mt-2">
          <button data-testid="interpret-btn" onClick={interpret} disabled={interpreting} className="text-sm px-4 py-2 rounded font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: GREEN }}>
            <Bot className={`h-4 w-4 ${interpreting ? "animate-spin" : ""}`} /> {interpreting ? "Interpreting…" : "Interpret with AI"}
          </button>
          <button data-testid="manual-campaign-btn" onClick={() => setBuilder({ name: "", criteria: EMPTY_CRITERIA })} className="text-sm px-4 py-2 rounded border border-slate-700 text-slate-200 flex items-center gap-1">
            <Pencil className="h-4 w-4" /> Build manually
          </button>
        </div>
      </Card>

      {builder && <CampaignBuilder initial={builder} saving={saving} onSave={save} onCancel={() => setBuilder(null)} />}

      {/* Saved campaigns */}
      <div className="grid md:grid-cols-2 gap-3" data-testid="campaigns-list">
        {data.campaigns.map((c) => {
          const crit = c.criteria || {};
          const running = runState[c.id];
          return (
            <Card key={c.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white">{c.name}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded ${c.status === "Paused" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{c.status}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">{c.geography} · {c.company_type || "Any type"} · {c.company_size} employees · min score {c.min_score}</div>
              <div className="flex flex-wrap gap-1 mt-2">{(c.products || []).slice(0, 6).map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-200">{p}</span>)}</div>
              <div className="text-xs text-slate-400 mt-2">Target people: <span className="text-slate-300">{fromArr((crit.person || {}).job_titles).slice(0, 90) || "—"}</span></div>
              <div className="text-xs text-slate-500 mt-1">Signals: {fromArr(crit.buying_signals).slice(0, 80) || "—"} · Prospects found: <b className="text-white">{c.prospects_found ?? 0}</b> · Apollo credits used: <b className="text-white">{c.apollo_credits_used ?? 0}</b></div>

              {(c.apollo_total_available > 0) && (() => {
                const pulled = c.apollo_offset || 0;
                const total = c.apollo_total_available || 0;
                const pct = total ? Math.min(100, Math.round((pulled / total) * 100)) : 0;
                return (
                  <div className="mt-2" data-testid={`coverage-${c.id}`}>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                      <span>Apollo coverage in this area</span>
                      <span data-testid={`coverage-label-${c.id}`}>Seen ~{pulled.toLocaleString()} of ~{total.toLocaleString()} matching companies · {pct}%</span>
                    </div>
                    <div className="h-1.5 rounded bg-slate-700 overflow-hidden">
                      <div className="h-full rounded transition-all" style={{ width: `${pct}%`, backgroundColor: GREEN }} />
                    </div>
                    {pulled >= total && <div className="text-[11px] text-emerald-400 mt-1">You've pulled every company Apollo has for this search.</div>}
                  </div>
                );
              })()}

              {running && <div className="text-xs text-emerald-400 mt-2 flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {running}</div>}

              <div className="flex flex-wrap gap-2 mt-3">
                <select data-testid={`depth-${c.id}`} value={depth[c.id] || 100} onChange={(e) => setDepth((d) => ({ ...d, [c.id]: Number(e.target.value) }))} disabled={!!running}
                  className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-slate-200 disabled:opacity-60" title="How many unique companies to pull per run">
                  {[25, 50, 100, 250].map((n) => <option key={n} value={n}>Pull {n} companies</option>)}
                </select>
                <button data-testid={`find-prospects-${c.id}`} onClick={() => run(c)} disabled={!!running || c.status === "Paused"}
                  className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: GREEN }}>
                  <Radar className="h-3.5 w-3.5" /> Run AI Prospect Search
                </button>
                <button data-testid={`results-${c.id}`} onClick={() => goToProspects(c.id)} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-200 flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Results</button>
                {(c.prospects_found > 0) && <button data-testid={`pull-more-${c.id}`} onClick={() => run(c)} disabled={!!running || c.status === "Paused"} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-200 flex items-center gap-1 disabled:opacity-60"><RefreshCw className="h-3.5 w-3.5" /> Pull more</button>}
                <button data-testid={`export-${c.id}`} onClick={() => exportCsv(c)} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-200 flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Export CSV</button>
                <button data-testid={`edit-${c.id}`} onClick={() => setBuilder({ id: c.id, name: c.name, criteria: crit, nl_query: c.nl_query })} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button data-testid={`duplicate-${c.id}`} onClick={() => action(c.id, "duplicate")} className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Duplicate</button>
                {c.status === "Paused"
                  ? <button data-testid={`resume-${c.id}`} onClick={() => action(c.id, "resume")} className="text-xs px-3 py-1.5 rounded border border-emerald-500/50 text-emerald-400">Resume</button>
                  : <button data-testid={`pause-${c.id}`} onClick={() => action(c.id, "pause")} className="text-xs px-3 py-1.5 rounded border border-amber-500/50 text-amber-400">Pause</button>}
                <button data-testid={`delete-${c.id}`} onClick={() => del(c.id)} className="text-xs px-3 py-1.5 rounded border border-red-500/50 text-red-400 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Agents() {
  const { data, loading } = useFetch("/agents");
  if (loading || !data) return <Loader />;
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-3">
        {data.agents.map((a) => (
          <Card key={a.name} className="p-4">
            <div className="flex items-center gap-2"><Bot className="h-4 w-4" style={{ color: GREEN }} /><span className="font-semibold text-white text-sm">{a.name}</span></div>
            <p className="text-xs text-slate-400 mt-1">{a.role}</p>
          </Card>
        ))}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Recent agent runs</h3>
        <Table testid="agent-runs" rows={data.runs} columns={[
          { key: "agent", label: "Agent", render: (r) => <span className="text-white">{r.agent}</span> },
          { key: "target", label: "Target" },
          { key: "status", label: "Status" },
        ]} />
      </div>
    </div>
  );
}

function Activity() {
  const { data, loading } = useFetch("/activity");
  if (loading || !data) return <Loader />;
  return (
    <Card className="p-4" >
      <ul className="space-y-2" data-testid="activity-list">
        {data.activity.map((a) => (
          <li key={a.id} className="flex items-start gap-3 text-sm border-b border-slate-800/60 pb-2">
            <span className="text-[10px] mt-1 px-2 py-0.5 rounded bg-slate-700 text-slate-300 shrink-0">{a.kind}</span>
            <span className="text-slate-300">{a.message}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Integrations() {
  const { data, loading } = useFetch("/integrations");
  if (loading || !data) return <Loader />;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="integrations-list">
      {data.integrations.map((i) => (
        <Card key={i.id} className="p-4 flex items-start justify-between">
          <div>
            <div className="font-semibold text-white flex items-center gap-2"><Plug className="h-4 w-4 text-slate-400" /> {i.name}</div>
            <div className="text-xs text-slate-400 mt-1">{i.category}{i.tier ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-emerald-300">{i.tier}</span> : null}</div>
            <p className="text-xs text-slate-500 mt-1">{i.description}</p>
          </div>
          <span className="text-[10px] px-2 py-1 rounded bg-slate-700 text-amber-400 shrink-0">{i.status}</span>
        </Card>
      ))}
    </div>
  );
}

function SuppressionPanel() {
  const { data, loading, reload } = useFetch("/suppression");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (payload) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/suppression`, payload, { headers: authHeaders() }); alert(`Added ${r.data.added} domain(s). Total: ${r.data.total}.`); setText(""); reload(); }
    catch (e) { alert(e.response?.data?.detail || "Could not add."); }
    setBusy(false);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => add({ csv: String(reader.result) });
    reader.readAsText(f);
  };
  const del = async (id) => { await axios.delete(`${API}/suppression/${id}`, { headers: authHeaders() }); reload(); };
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: GREEN }} /> Do-Not-Contact List (Suppression)</h3>
      <p className="text-xs text-slate-500 mb-3">Add existing customers by <b>email domain</b> (e.g. <code>acme.co.uk</code>). Any Apollo prospect on these domains is skipped during discovery.</p>
      <textarea data-testid="suppression-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="acme.co.uk, globex.com  (comma or newline separated)" className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white h-20" />
      <div className="flex flex-wrap gap-2 mt-2">
        <button data-testid="suppression-add" onClick={() => add({ entries: text.split(/[,\n]/) })} disabled={busy || !text.trim()} className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ backgroundColor: GREEN }}><Check className="h-3.5 w-3.5" /> Add domains</button>
        <label className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-200 flex items-center gap-1 cursor-pointer">
          <Upload className="h-3.5 w-3.5" /> Upload CSV
          <input data-testid="suppression-csv" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
      </div>
      <div className="mt-3 max-h-56 overflow-auto" data-testid="suppression-list">
        {loading || !data ? <Loader /> : (data.suppression.length === 0
          ? <p className="text-xs text-slate-500">No suppressed domains yet.</p>
          : data.suppression.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60">
              <span className="text-slate-200">{s.domain} <span className="text-[10px] text-slate-500">· {s.label}</span></span>
              <button onClick={() => del(s.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )))}
      </div>
    </Card>
  );
}

function SettingsView() {
  const weights = [["Company Fit", 20], ["Decision Maker Fit", 15], ["Current Project / Requirement", 20], ["Buying Signal", 15], ["ZKTeco Product Fit", 15], ["Timing", 10], ["Research Confidence", 5]];
  return (
    <div className="space-y-4">
      <SuppressionPanel />
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: GREEN }} /> Opportunity Scoring Model</h3>        {weights.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60">
            <span className="text-slate-300">{k}</span><span className="text-white font-semibold">{v} pts</span>
          </div>
        ))}
        <p className="text-xs text-slate-500 mt-3">Scoring weights become editable in a later release.</p>
      </Card>
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-white mb-2">Ideal Customer Profile (ICP)</h3>
        <p className="text-sm text-slate-300">UK B2B security industry — integrators (access control, CCTV, entrance control, biometrics) and end-user markets (universities, NHS, government, commercial, logistics), plus specifiers/influencers (architects, consultants, FM, main contractors).</p>
      </Card>
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-white mb-2">AI Engine &amp; Outreach Workflow</h3>
        <p className="text-sm text-slate-300">Research, scoring rationale and outreach are generated by <b className="text-white">Claude Sonnet 4.6</b> via the Emergent Universal LLM key.</p>
        <p className="text-sm text-slate-300 mt-2">Workflow: <span className="text-slate-400">Research → Analyse → Personalise → Draft → Review → <b className="text-white">Copy / Open email client</b> → send manually → <b className="text-white">Mark as Contacted</b></span>. There is <b className="text-white">no automatic sending</b> — you send every email yourself from your own mailbox.</p>
        <p className="text-sm text-slate-300 mt-2">Primary real-data source: <b className="text-white">Apollo</b> (once connected). LinkedIn/Sales Navigator is used as an intelligence source via authorised methods only. Clay &amp; Cognism are optional future sources. Until connected, the desk uses <span className="text-amber-400 font-semibold">DEMO DATA</span>.</p>
      </Card>
    </div>
  );
}

function CompanyDetail({ id, onBack }) {
  const { data, loading, reload } = useFetch(`/prospects/${id}`);
  const [busy, setBusy] = useState("");
  if (loading || !data) return <Loader />;
  const c = data.company;
  const b = scoreBadge(c.opportunity_score);

  const regen = async () => {
    setBusy("research");
    try { await axios.post(`${API}/generate/research/${id}`, {}, { headers: authHeaders() }); await reload(); }
    catch (e) { alert(e.response?.data?.detail || "AI failed"); }
    setBusy("");
  };
  const genOutreach = async (contactId) => {
    setBusy("outreach-" + contactId);
    try { await axios.post(`${API}/generate/outreach/${contactId}`, {}, { headers: authHeaders() }); await reload(); alert("Outreach drafted — see the Outreach tab (Awaiting Approval)."); }
    catch (e) { alert(e.response?.data?.detail || "AI failed"); }
    setBusy("");
  };
  const enrichEmail = async (contactId) => {
    setBusy("enrich-" + contactId);
    try {
      const res = await axios.post(`${API}/contacts/${contactId}/enrich-email`, {}, { headers: authHeaders() });
      alert(res.data.status === "ok" ? `Verified email: ${res.data.email}` : (res.data.message || "No email match."));
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Enrichment failed"); }
    setBusy("");
  };
  const researchIndividual = async (contactId) => {
    setBusy("indiv-" + contactId);
    try {
      await axios.post(`${API}/contacts/${contactId}/research-individual`, {}, { headers: authHeaders() });
      await reload();
    } catch (e) { alert(e.response?.data?.detail || "Individual research failed"); }
    setBusy("");
  };
  const refreshSignals = async () => {
    setBusy("signals");
    try { await axios.post(`${API}/companies/${id}/refresh-signals`, {}, { headers: authHeaders() }); await reload(); }
    catch (e) { alert(e.response?.data?.detail || "Signal research failed"); }
    setBusy("");
  };
  const startQuote = () => {
    const prods = [c.recommended_product, ...(c.product_fit || [])].filter(Boolean).join(", ").toLowerCase();
    let route = "/dashboard";
    if (/full.?height/.test(prods)) route = "/full-height-turnstiles";
    else if (/turnstile|speed|entrance|pedestrian|lane|gate/.test(prods)) route = "/pedestrian-entrance-control";
    else if (/biocv|biometric|face|palm/.test(prods)) route = "/bio-cvsecurity";
    else if (/access|atlas/.test(prods)) route = "/access-control";
    const brief = `ZKTeco opportunity — ${c.name}\nLocation: ${c.location}\nRecommended: ${c.recommended_product || (c.product_fit || []).join(", ")}\nBuying signal: ${c.buying_signal || "—"}\nWhy: ${c.why_contact || ""}`;
    localStorage.setItem("salesdesk_quote_context", JSON.stringify({ company: c.name, products: c.product_fit, recommended: c.recommended_product, ts: Date.now() }));
    if (navigator.clipboard) navigator.clipboard.writeText(brief).catch(() => {});
    window.open(route, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="company-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ChevronLeft className="h-4 w-4" /> Back</button>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">{c.name}</h2>
            <div className="text-xs text-slate-400">{[...new Set([c.website, c.sector, c.company_type, c.location, c.employee_count ? `${c.employee_count} employees` : null].filter(Boolean))].join(" · ")}</div>
          </div>
          <div className="text-right">
            <span className={`text-[10px] font-bold px-2 py-1 rounded ${b.c}`}>{b.t}</span>
            <div className="text-xl font-bold mt-1" style={{ color: GREEN }}>{c.opportunity_score}/100</div>
            <div className="text-[11px] text-slate-500">AI confidence {c.ai_confidence}%</div>
          </div>
        </div>
        {(c.buying_signal || c.recommended_product) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {c.buying_signal && <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-300" data-testid="company-signal">
              <Flame className="h-3 w-3 inline mr-1" />{c.buying_signal}{c.buying_signal_status ? ` · ${c.buying_signal_status}` : ""}{c.buying_signal_confidence ? ` (${c.buying_signal_confidence}%)` : ""}</span>}
            {c.signal_source && <span className={`px-2 py-1 rounded ${c.signal_source.includes("live") ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/40 text-slate-300"}`}>{c.signal_source.includes("live") ? "Live (SerpApi)" : "AI estimate"}</span>}
            {c.buying_signal_source_url && <a href={c.buying_signal_source_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded bg-sky-500/15 text-sky-300 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Source{c.buying_signal_source_date ? ` · ${c.buying_signal_source_date}` : ""}</a>}
            {c.recommended_product && <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300">Rec: {c.recommended_product}</span>}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="refresh-signals" onClick={refreshSignals} disabled={busy === "signals"}
            className="text-xs px-3 py-1.5 rounded border border-amber-500/50 text-amber-400 flex items-center gap-1">
            <Flame className={`h-3.5 w-3.5 ${busy === "signals" ? "animate-pulse" : ""}`} /> {busy === "signals" ? "Researching…" : "Refresh buying signal (AI)"}
          </button>
          <button data-testid="start-quote" onClick={startQuote}
            className="text-xs px-3 py-1.5 rounded font-semibold text-white flex items-center gap-1" style={{ backgroundColor: GREEN }}>
            <Target className="h-3.5 w-3.5" /> Start Quote in Configurator
          </button>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: GREEN }} /> Why Contact Them?</h3>
              <button data-testid="regen-research" onClick={regen} disabled={busy === "research"} className="text-xs flex items-center gap-1 text-slate-300 hover:text-white">
                <RefreshCw className={`h-3.5 w-3.5 ${busy === "research" ? "animate-spin" : ""}`} /> {busy === "research" ? "Generating…" : "Regenerate (AI)"}
              </button>
            </div>
            <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">{c.why_contact}</pre>
          </Card>

          {(c.company_fit || c.opportunity_fit || c.recommended_product || c.buying_signal) && (
            <Card className="p-5" data-testid="ai-qualification">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: GREEN }} /> AI Qualification</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[["Company fit", c.company_fit], ["Person fit", c.person_fit], ["Opportunity fit", c.opportunity_fit], ["Timing", c.timing], ["Product fit", c.product_fit_text], ["Recommended product", c.recommended_product], ["Relevant project", c.relevant_project], ["Buying signal", c.buying_signal]].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[11px] text-slate-400 uppercase tracking-wide">{k}</div>
                    <div className="text-slate-200">{v}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5">
            <h3 className="text-sm font-bold text-white mb-1">What They Do</h3>
            <p className="text-sm text-slate-300 mb-3">{c.what_they_do}</p>
            <h3 className="text-sm font-bold text-white mb-1">Research Summary</h3>
            <p className="text-sm text-slate-300">{c.research_summary}</p>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-bold text-white mb-2">Existing Technology</h3>
            <div className="flex flex-wrap gap-2">
              {c.existing_technology.map((t, i) => (
                <span key={i} className={`text-xs px-2 py-1 rounded ${confColor(t.confidence)}`}>{t.name} · {t.confidence}</span>
              ))}
            </div>
            <h3 className="text-sm font-bold text-white mt-4 mb-2">Buying Signals</h3>
            <ul className="space-y-2">
              {data.signals.map((s) => (
                <li key={s.id} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${confColor(s.confidence)} shrink-0 mt-0.5`}>{s.confidence}</span>
                  <span>{s.finding} <span className="text-slate-500">· {s.category} · strength {s.strength}</span></span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="text-sm font-bold text-white mb-2">ZKTeco Product Fit</h3>
            <div className="flex flex-wrap gap-1.5">
              {c.product_fit.map((p) => <span key={p} className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-300">{p}</span>)}
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="text-sm font-bold text-white mb-3">Decision Makers</h3>
            <div className="space-y-3">
              {data.contacts.map((p) => (
                <div key={p.id} className="border-b border-slate-800/60 pb-3">
                  <div className="text-sm text-white font-medium">{p.full_name}</div>
                  <div className="text-xs text-slate-400">{p.job_title}</div>
                  <div className="text-xs text-slate-400">{p.email_locked ? <span className="text-amber-400">🔒 Email locked</span> : (p.verification_status === "UNVERIFIED" ? <span className="text-amber-400">{p.email}</span> : p.email)}{p.phone ? ` · ${p.phone}` : ""}</div>
                  {p.email_locked && (
                    <button data-testid={`enrich-${p.id}`} onClick={() => enrichEmail(p.id)} disabled={busy === "enrich-" + p.id}
                      className="mt-1.5 text-xs font-semibold flex items-center gap-1 text-sky-400">
                      <Lock className="h-3.5 w-3.5" /> {busy === "enrich-" + p.id ? "Unlocking…" : "Unlock verified email (Apollo)"}
                    </button>
                  )}
                  {p.linkedin && <a href={p.linkedin} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-sky-400 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> LinkedIn profile</a>}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {p.apollo_person_id && (
                      <button data-testid={`research-indiv-${p.id}`} onClick={() => researchIndividual(p.id)} disabled={busy === "indiv-" + p.id}
                        className="text-xs font-semibold flex items-center gap-1 text-sky-400">
                        <Bot className={`h-3.5 w-3.5 ${busy === "indiv-" + p.id ? "animate-spin" : ""}`} /> {busy === "indiv-" + p.id ? "Researching…" : (p.research_done ? "Re-research individual" : "Research individual (LinkedIn)")}
                      </button>
                    )}
                    <button data-testid={`gen-outreach-${p.id}`} onClick={() => genOutreach(p.id)} disabled={busy === "outreach-" + p.id}
                      className="text-xs font-semibold flex items-center gap-1" style={{ color: GREEN }}>
                      <Sparkles className="h-3.5 w-3.5" /> {busy === "outreach-" + p.id ? "Writing…" : "Generate personalised outreach"}
                    </button>
                  </div>
                  {p.individual_research && (
                    <pre data-testid={`indiv-briefing-${p.id}`} className="mt-2 text-[11px] text-slate-300 whitespace-pre-wrap font-sans bg-slate-900/60 rounded p-2 border border-slate-800">{p.individual_research}</pre>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Loader() {
  return <div className="flex items-center justify-center py-20 text-slate-500"><RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
}
